/**
 * Agreement Card Component
 *
 * Displays a single agreement with type, description, deadline, and status.
 */

import type { EmployeeAgreement } from '../../lib/agreementsApi';

interface AgreementCardProps {
  agreement: EmployeeAgreement;
  showEvaluation?: boolean;
}

export function AgreementCard({ agreement, showEvaluation = false }: AgreementCardProps) {
  const getColorClasses = (color: string): { bg: string; text: string; border: string } => {
    const colors = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
      green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
      teal: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
      gray: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' },
    } as const;
    return colors[color as keyof typeof colors] || colors.gray;
  };

  const colorClasses = getColorClasses(agreement.typeColor);
  const isOverdue = agreement.deadline && !agreement.isCompleted && new Date(agreement.deadline) < new Date();

  return (
    <div className={`border ${colorClasses.border} rounded-lg p-4 ${colorClasses.bg} transition-shadow hover:shadow-md`}>
      {/* Header with type and status */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${colorClasses.bg} ${colorClasses.text} border ${colorClasses.border}`}>
            <span className="mr-1.5">{agreement.typeIcon}</span>
            {agreement.typeName}
          </span>

          {agreement.isCompleted && (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 border border-green-200 rounded-full flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Afgerond
            </span>
          )}

          {isOverdue && (
            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 border border-red-200 rounded-full flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Verlopen
            </span>
          )}
        </div>

        {agreement.deadline && (
          <div className="text-right">
            <div className="text-xs text-gray-500">Deadline</div>
            <div className={`text-sm font-medium ${isOverdue && !agreement.isCompleted ? 'text-red-600' : 'text-gray-700'}`}>
              {new Date(agreement.deadline).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-gray-900 mb-3 whitespace-pre-wrap">{agreement.beschrijving}</p>

      {/* Evaluation period (if shown) */}
      {showEvaluation && agreement.evaluationPeriod && (
        <div className="pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>
              Evaluatieperiode: {new Date(agreement.evaluationPeriod.start).toLocaleDateString('nl-NL')} - {new Date(agreement.evaluationPeriod.end).toLocaleDateString('nl-NL')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
